import { Info } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./tooltip";

/**
 * A small, discoverable help affordance: an ⓘ icon placed next to a label or title
 * that reveals a short explanation on hover OR keyboard focus. Unlike a bare tooltip
 * (which nobody knows is there), the icon signals that help exists. Accessible: it's a
 * real focusable button with an aria-label, and Radix opens the tooltip on focus too.
 *
 * Usage: <span className="flex items-center gap-1">Label <InfoHint text="…" /></span>
 */
export function InfoHint({
  text,
  side = "top",
  label = "More information",
  className,
}: {
  text: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  /** Accessible name for the icon button (defaults to "More information"). */
  label?: string;
  className?: string;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            data-testid="info-hint"
            // Don't let a click bubble to a surrounding row/link or submit a form.
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={cn(
              "inline-flex h-[15px] w-[15px] shrink-0 cursor-help items-center justify-center rounded-full align-middle text-ge-muted transition-colors hover:text-ge-value focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ge-accent",
              className,
            )}
          >
            <Info className="h-[13px] w-[13px]" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side}>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
